import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

// Функция для очистки HTML и защиты от XSS
const sanitizeInput = (input: string): string => {
  return input
    .replace(/[<>'"&]/g, '') // Удаляем потенциально опасные символы
    .trim()
    .substring(0, 100); // Ограничиваем длину
};

// Функция для валидации пароля
const validatePassword = (password: string): { isValid: boolean; error?: string } => {
  if (!password || password.length === 0) {
    return { isValid: false, error: 'Пароль обязателен' };
  }
  
  if (password.length > 100) {
    return { isValid: false, error: 'Пароль слишком длинный' };
  }
  
  if (/[<>'"&]/.test(password)) {
    return { isValid: false, error: 'Пароль содержит недопустимые символы' };
  }
  
  return { isValid: true };
};

const Login: React.FC = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lockoutTime, setLockoutTime] = useState<number | null>(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Редирект на главную страницу если пользователь уже авторизован
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Проверка блокировки
  useEffect(() => {
    const checkLockout = () => {
      if (lockoutTime && Date.now() < lockoutTime) {
        const remainingTime = Math.ceil((lockoutTime - Date.now()) / 1000 / 60);
        setError(`Слишком много попыток входа. Попробуйте через ${remainingTime} минут.`);
      } else if (lockoutTime && Date.now() >= lockoutTime) {
        setLockoutTime(null);
        setError('');
      }
    };

    const interval = setInterval(checkLockout, 1000);
    return () => clearInterval(interval);
  }, [lockoutTime]);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setPassword(sanitizedValue);
    
    // Очищаем ошибки при изменении пароля
    if (error) {
      setError('');
    }
  }, [error]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Проверяем блокировку
    if (lockoutTime && Date.now() < lockoutTime) {
      return;
    }
    
    // Валидация на клиенте
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError(validation.error || 'Неверный формат пароля');
      return;
    }
    
    setError('');
    setIsLoading(true);

    try {
      const result = await login(password);
      if (result.success) {
        // Автоматический редирект на главную страницу после успешного логина
        navigate('/', { replace: true });
      } else {
        const errorMessage = result.error || 'Произошла ошибка при входе';
        setError(errorMessage);
        
        // Проверяем, есть ли информация о блокировке в ответе
        if (errorMessage.includes('минут') && errorMessage.includes('попыток')) {
          const match = errorMessage.match(/(\d+)/);
          if (match) {
            const minutes = parseInt(match[1]);
            setLockoutTime(Date.now() + minutes * 60 * 1000);
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Произошла неожиданная ошибка. Попробуйте еще раз.');
    } finally {
      setIsLoading(false);
    }
  }, [password, login, navigate, lockoutTime]);

  const handleRetry = useCallback(() => {
    if (lockoutTime && Date.now() < lockoutTime) {
      return; // Не позволяем повторные попытки во время блокировки
    }
    
    setRetryCount(prev => prev + 1);
    setError('');
    handleSubmit(new Event('submit') as any);
  }, [lockoutTime, handleSubmit]);

  const isLockedOut = Boolean(lockoutTime && Date.now() < lockoutTime);
  const isFormDisabled = isLoading || isLockedOut;

  return (
    <div className="login-container">
      <div className="login-form">
        <h1>Вход в систему администрирования</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">Пароль:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              required
              disabled={isFormDisabled}
              placeholder="Введите пароль"
              maxLength={100}
              autoComplete="current-password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>
          {error && (
            <div className="error-message">
              <span dangerouslySetInnerHTML={{ __html: error }} />
              {retryCount < 3 && !isLockedOut && (
                <button 
                  type="button" 
                  onClick={handleRetry}
                  className="retry-button"
                  disabled={isFormDisabled}
                >
                  Попробовать снова
                </button>
              )}
            </div>
          )}
          <button 
            type="submit" 
            disabled={isFormDisabled}
            style={{ 
              opacity: isFormDisabled ? 0.6 : 1,
              cursor: isFormDisabled ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'Вход...' : isLockedOut ? 'Заблокировано' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
